import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { DateInput, DateSegment, TimeField } from 'react-aria-components';
import { parseTime, Time } from '@internationalized/date';

const START_MINUTES = 6 * 60;
const END_MINUTES = 5 * 60 + 30;
const STEP_MINUTES = 30;

const padTimePart = (value) => String(value).padStart(2, '0');

const formatTimeValue = (time) => `${padTimePart(time.hour)}:${padTimePart(time.minute)}`;

const formatTimeLabel = (value) => {
  const [hourPart, minutePart] = value.split(':');
  const hour = Number.parseInt(hourPart, 10);
  const minute = Number.parseInt(minutePart, 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return value;
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  const adjustedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${padTimePart(adjustedHour)}:${padTimePart(minute)} ${meridiem}`;
};

const minutesToValue = (minutes) => {
  const hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  return `${padTimePart(hour)}:${padTimePart(minute)}`;
};

const generateTimeOptions = () => {
  const options = [];
  for (let minutes = START_MINUTES; minutes < 24 * 60; minutes += STEP_MINUTES) {
    const value = minutesToValue(minutes);
    options.push({ value, label: formatTimeLabel(value) });
  }
  for (let minutes = 0; minutes <= END_MINUTES; minutes += STEP_MINUTES) {
    const value = minutesToValue(minutes);
    options.push({ value, label: formatTimeLabel(value) });
  }
  return options;
};

const isWithinRange = (time) => {
  const minutes = time.hour * 60 + time.minute;
  return minutes >= START_MINUTES || minutes <= END_MINUTES;
};

const TimePickerBootstrap = ({
  value,
  onChange,
  disabled = false,
  className = '',
  ariaLabel = 'Time picker',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(value ? parseTime(value) : null);
  const containerRef = useRef(null);
  const timeOptions = useMemo(() => generateTimeOptions(), []);
  const invalidClass = className.split(' ').includes('is-invalid') ? 'is-invalid' : '';

  useEffect(() => {
    if (value) {
      setInternalValue(parseTime(value));
    } else {
      setInternalValue(null);
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleValueChange = (nextValue) => {
    if (!nextValue) {
      setInternalValue(null);
      onChange(null);
      return;
    }
    const exactTime = new Time(nextValue.hour, nextValue.minute);
    setInternalValue(exactTime);
    if (!isWithinRange(exactTime)) {
      onChange(null);
      return;
    }
    onChange(formatTimeValue(exactTime));
  };

  const handleSelect = (nextValue) => {
    const parsed = parseTime(nextValue);
    handleValueChange(parsed);
    setIsOpen(false);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setInternalValue(null);
    onChange(null);
    setIsOpen(false);
  };

  return (
    <div className={`dropdown ${className}`} ref={containerRef}>
      <TimeField
        value={internalValue}
        onChange={handleValueChange}
        granularity="minute"
        hourCycle={12}
        isDisabled={disabled}
        aria-label={ariaLabel}
      >
        <div
          className={`form-control d-flex align-items-center px-8 gap-1 ${disabled ? 'bg-light' : ''} ${invalidClass}`}
          style={{ width: "180px" }}
          onClick={() => {
            if (!disabled) setIsOpen(true);
          }}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="text-secondary d-flex align-items-center">
            <Icon icon="mdi:clock-outline" className="text-xl"/>
          </span>
          <DateInput className="d-flex align-items-center flex-grow-1">
            {(segment) => (
              <DateSegment
                segment={segment}
                className="px-1 rounded"
                style={{ minWidth: '1.6rem', textAlign: 'center' }}
              />
            )}
          </DateInput>
          {internalValue && !disabled ? (
            <button
              type="button"
              className="btn btn-link text-decoration-none text-secondary p-0"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleClear}
              aria-label="Clear time"
            >
              <Icon 
                icon="mdi:close" 
                style={{ position: "relative", top: "-1px", fontSize: "20px" }}/>
            </button>
          ) : null}
        </div>
      </TimeField>
      <ul
        className={`dropdown-menu ${isOpen ? 'show' : ''}`}
        style={{ width: '100%', maxHeight: '240px', overflowY: 'auto' }}
        role="listbox"
      >
        {timeOptions.map((option) => (
          <li key={option.value}>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TimePickerBootstrap;
